import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { CheckoutStorageService, Oauth2Service } from '@bytel/checkout';

import { PtoStep } from '@shared/stepper/steps/pto.step';

import { Observable, Subject, of } from 'rxjs';
import { catchError, filter, mergeMap } from 'rxjs/operators';

import { ErrorModalRetryComponent } from '../shared/error-modal-retry/error-modal-retry.component';
import { AppointmentStep } from '../shared/stepper/steps/appointment.step';
import { CartStep } from '../shared/stepper/steps/cart.step';
import { CrossellStep } from '../shared/stepper/steps/crossell.step';
import { DeliveryStep } from '../shared/stepper/steps/delivery.step';
import { InstallationStep } from '../shared/stepper/steps/installation.step';
import { PaymentStep } from '../shared/stepper/steps/payment.step';
import { SignupStep } from '../shared/stepper/steps/signup.step';
import { SirenStep } from '../shared/stepper/steps/siren.step';
import { Step } from '../shared/stepper/steps/step';
import { MessageService } from './message.service';

// @dynamic
@Injectable({
    providedIn: 'root',
})
export class StepperService {
    public allSteps: Step[] = [
        this.cartStep,
        this.crosssellStep,
        this.sirenStep,
        this.signupStep,
        this.ptoStep,
        this.appointmentStep,
        this.installationStep,
        this.deliveryStep,
        this.paymentStep,
    ];
    public comesFromNextStep: boolean;
    public steps: Step[];

    public validateSubject: Subject<boolean> = new Subject<boolean>();
    public stepsUpdateSubject: Subject<boolean> = new Subject<boolean>();
    public currentStepSubject: Subject<Step> = new Subject<Step>();
    public showValidationErrorsSubject: Subject<boolean> = new Subject<boolean>();
    public currentRoute: string;
    protected checkoutStorage = inject(CheckoutStorageService);
    private _currentStep: Step;
    private previousUrl: string;
    private currentStepKey = 'current_step';

    public constructor(
        // NOSONAR
        private cartStep: CartStep,
        private crosssellStep: CrossellStep,
        private ptoStep: PtoStep,
        private signupStep: SignupStep,
        private appointmentStep: AppointmentStep,
        private deliveryStep: DeliveryStep,
        private paymentStep: PaymentStep,
        private sirenStep: SirenStep,
        public router: Router,
        private oauth2Service: Oauth2Service,
        private messageService: MessageService,
        private installationStep: InstallationStep,
        @Inject(DOCUMENT) private document: Document,
    ) {
        this.currentStep = this.allSteps[0];
        this.initCurrentSteps(this.allSteps);
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((e) => {
            this.checkoutStorage.set(this.currentStepKey, this.currentStep.key);
            const event: NavigationEnd = e as NavigationEnd;
            this.oauth2Service.updateIsConnected();
            this.currentRoute = event.url;
            if (this.getNextStep()) {
                this.comesFromNextStep = this.previousUrl === this.getNextStep().routerLink;
            }
            this.previousUrl = event.url;
            this.document.defaultView.window.scroll(0, 0);

            if (event.url.includes('/panier')) {
                this.currentStep = this.allSteps[0];
                this.checkoutStorage.set(this.currentStepKey, this.currentStep.key);
            }
        });
    }

    set currentStep(step: Step) {
        this._currentStep = step;
        this.currentStepSubject.next(step);
    }

    get currentStep(): Step {
        return this._currentStep;
    }

    public getNextStep(): Step {
        return this.currentStep.next;
    }

    public initNextStep(): Observable<boolean> {
        this.currentStep.isValid = true;
        this.currentStep = this.currentStep.nextStep();

        const obs = this.currentStep.isBypass ? this.currentStep.bypass() : of(false);
        return obs.pipe(
            mergeMap((bypass) => (bypass ? this.initNextStep() : of(true))),
            catchError(() => {
                this.currentStep = this.currentStep.previous;
                this.messageService.openModal(ErrorModalRetryComponent, null, null, {
                    retryEvent: () => this.initNextStep().subscribe(),
                });
                return of(false);
            }),
        );
    }

    public goToNextStep(): void {
        this.router.navigateByUrl(this.currentStep.routerLink);
    }

    public goToPreviousStep(): void {
        this.currentStep.previousStep();
        this.currentStep = this.currentStep.previous;
    }

    public goToStep(step: Step): void {
        this.allSteps.filter((s) => s.order < step.order).forEach((s) => (s.isValid = true));
        this.currentStep = step;
        step.setCurrentStep();
        this._invalideAllNext();
    }

    public refresh(): void {
        this.initCurrentSteps(this.allSteps);
        this.stepsUpdateSubject.next(true);
    }


    public triggerValidationErrors(): void {
        this.showValidationErrorsSubject.next(true);
        this.scrollToFirstError();
    }

    public getPreviousStepsReminders(): Step[] {
        const currentStepIndexInAllSteps: number = this.allSteps.indexOf(this.currentStep);
        return this.allSteps.filter((step: Step, stepIndex: number) => {
            const canDisplay: boolean = step.canDisplay();
            const shouldForceReminder: boolean = !step.canDisplay() && step.forceReminder();
            return (
                stepIndex < currentStepIndexInAllSteps &&
                (canDisplay || shouldForceReminder) &&
                step.hasReminder &&
                !step.isBypass
            );
        });
    }

    public getNextStepsReminders(): Step[] {
        const nextStpes: Step[] = this._getNextSteps();
        return nextStpes.filter((step: Step) => step.hasReminder && !step.isBypass);
    }

    public initCurrentSteps(steps: Step[]): void {
        let previousStep: Step = null;
        this.steps = [];

        steps.forEach((step, index) => {
            step.setByPass();
            if (step.canDisplay()) {
                if (previousStep) {
                    previousStep.next = step;
                    step.previous = previousStep;
                }
                step.order = index;
                this.steps.push(step);
                previousStep = step;
            }
        });
    }

    private _invalideAllNext(): void {
        let step: Step = this.currentStep;
        while (step.next) {
            step.isValid = false;
            step.saveToStorage();
            step = step.next;
        }
        step.isValid = false;
        step.saveToStorage();
    }

    private _getNextSteps(): Step[] {
        let step: Step = this.currentStep;
        const result: Step[] = [];
        while (step.next) {
            result.push(step.next);
            step = step.next;
        }
        return result;
    }

    public scrollToFirstError(): void {
        setTimeout(() => {
            const errorSelectors = [
                '.help.is-error',
                '.text.help.is-error',
                '.is-error .help',
                '.field.is-error',
                '.help.is-error',
                '.control.is-error',
                '.error-block',
                '.alert.is-danger',
                '[class*="error"]',
            ];

            let firstErrorElement: Element | null = null;

            for (const selector of errorSelectors) {
                firstErrorElement = this.document.querySelector(selector);
                if (firstErrorElement) break;
            }

            if (firstErrorElement) {
                // Calculate scroll position with offset for better UX
                const elementTop =
                    firstErrorElement.getBoundingClientRect().top + this.document.defaultView.window.pageYOffset;
                const offset = 120; // Offset to account for fixed headers

                this.document.defaultView.window.scrollTo({
                    top: Math.max(0, elementTop - offset),
                    behavior: 'smooth',
                });

                // Try to focus the associated input for accessibility
                this._focusErrorField(firstErrorElement);
            }
        }, 150); // Delay to ensure error messages are rendered
    }

    private _focusErrorField(errorElement: Element): void {
        const fieldContainer = errorElement.closest('.field, .control, .form-group');
        if (fieldContainer) {
            const inputElement = fieldContainer.querySelector('input, textarea, select') as HTMLInputElement;
            if (inputElement && inputElement.focus && !inputElement.disabled && !inputElement.readOnly) {
                // Delay focus to allow scroll to complete
                setTimeout(() => {
                    inputElement.focus();
                }, 400);
            }
        }
    }
}
